import { useState, type ReactNode } from 'react';
import { useConfigurationTheme, useSidebar } from '@portal/platform/configuration';
import { AppLink } from '../app-link';
import { fill } from '../i18n/shell-texts';
import { useShellTexts } from '../i18n/use-shell-texts';
import { Breadcrumb } from './breadcrumb';
import { usePortalBranding } from './branding';
import { useCollapsedMenu } from './nav-preferences';
import { Sidebar, type NavBadges } from './sidebar';
import { LOGOUT_ITEM_ID, UserMenu } from './user-menu';

/** The mobile menu toggle: a bare ☰, faint until hovered. */
const togglerClass =
  'flex min-w-[50px] items-center justify-center self-stretch px-3 text-xl text-black/30 hover:text-black/60 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-input-focus';

/**
 * The authenticated frame: a fixed header (brand, the user menu — which also holds an admin's
 * view mode), the backend-driven sidebar — which also holds the org node the portal is scoped to — a breadcrumb,
 * the page, and a footer. Navigation design A (LEGACY_PARITY_RUNS.md P7); the earlier layout was
 * ported from portal-ui-lib's LayoutComponent + HeaderComponent.
 *
 * `keepUnmatched` keeps sidebar entries whose page is not in this app yet (they land on the
 * app's legacy placeholder); see `SidebarOptions`. `defaultPins` and `badges` are the app's: which
 * entries start pinned, and their waiting-item counts.
 */
export function AppShell({
  brand,
  onLogout,
  keepUnmatched = false,
  defaultPins,
  badges,
  children,
}: {
  /** The header's brand, linking home. */
  brand: ReactNode;
  onLogout: () => void;
  keepUnmatched?: boolean;
  defaultPins?: readonly string[];
  badges?: NavBadges;
  children: ReactNode;
}) {
  useConfigurationTheme();
  const t = useShellTexts();
  const branding = usePortalBranding();
  const { sections, headerItems } = useSidebar({ keepUnmatched });
  const [collapsed, setCollapsed] = useCollapsedMenu();
  const [mobileOpen, setMobileOpen] = useState(false);
  const logoutLabel = headerItems.find(item => item.id === LOGOUT_ITEM_ID)?.label;

  return (
    <div className="relative min-h-dvh bg-page">
      <header className="fixed inset-x-0 top-0 z-[350] flex h-header items-stretch border-b border-line bg-white">
        <button
          type="button"
          aria-expanded={mobileOpen}
          aria-label={mobileOpen ? t.sidebar.hide : t.sidebar.show}
          onClick={() => setMobileOpen(value => !value)}
          className={`${togglerClass} lg:hidden`}
        >
          <span aria-hidden="true">&#9776;</span>
        </button>
        <AppLink href="/" className="flex min-w-0 shrink items-center px-2 no-underline lg:px-4">
          {brand}
        </AppLink>
        <div className="ml-auto flex items-center gap-2 pr-4">
          <div className="max-lg:hidden">
            <UserMenu items={headerItems} onLogout={onLogout} />
          </div>
        </div>
      </header>
      <Sidebar
        sections={sections}
        collapsed={collapsed}
        onCollapsedChange={setCollapsed}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
        logoutLabel={logoutLabel}
        onLogout={onLogout}
        defaultPins={defaultPins}
        badges={badges}
      />
      <main
        className={`min-h-dvh pt-header pb-footer transition-[padding-left] duration-[250ms] ${collapsed ? 'lg:pl-rail' : 'lg:pl-sidebar'}`}
      >
        <Breadcrumb sections={sections} />
        <div className="px-gutter">{children}</div>
      </main>
      <footer
        className={`absolute right-0 bottom-0 left-0 hidden h-footer justify-between px-4 leading-[50px] transition-[left] duration-[250ms] lg:flex ${collapsed ? 'lg:left-rail' : 'lg:left-sidebar'}`}
      >
        <span>{fill(t.footer.copyright, { year: String(new Date().getFullYear()) })}</span>
        {branding.name ? <span>{fill(t.footer.poweredBy, { brand: branding.name })}</span> : null}
      </footer>
    </div>
  );
}
